import React, { useState, useEffect, useContext } from "react";
import {
    makeStyles,
    Paper,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    Switch,
    Tooltip
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import {
    Edit as EditIcon,
    Check as CheckIcon,
    Remove as RemoveIcon
} from "@material-ui/icons";

import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { StatusBadge, Section } from "../ui";
import {
    usePanelStyles,
    FormField,
    FormSection,
    SwitchRow
} from "../QueueIntegrationModal/panelKit";
import { getTokens } from "../../styles/tokens";
import { isSuperUser } from "../../utils/tenant";

// Revenda: plano da plataforma = companyId null (somente-leitura para revendedor)
const isPlatformPlan = (plan) => plan && (plan.companyId === null || plan.companyId === undefined);


const useStyles = makeStyles(theme => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        root: {
            width: '100%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
        },
        fullWidth: {
            width: '100%'
        },
        // formulário em página (não em modal): colunas conforme a largura disponível
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            columnGap: 16,
            rowGap: 14,
            alignItems: 'start'
        },
        nameField: {
            gridColumn: 'span 2',
            [theme.breakpoints.down('xs')]: { gridColumn: 'auto' }
        },
        flagsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: 10
        },
        formActions: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 20,
            paddingTop: 16,
            borderTop: `1px solid ${t.line}`
        },
        formActionsRight: {
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8
        },
        // colunas nunca quebram; a tabela rola na horizontal dentro do cartão
        table: {
            '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
            // a célula presa tem fundo próprio: acompanha o realce da linha
            '& tbody .MuiTableRow-root:hover $actionsCell': {
                backgroundImage: `linear-gradient(${t.rowHover}, ${t.rowHover})`
            }
        },
        colName: { minWidth: 200 },
        nameCell: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8
        },
        flagOn: { fontSize: 18, verticalAlign: 'middle', color: t.status.ok.fg },
        flagOff: { fontSize: 18, verticalAlign: 'middle', color: t.textMuted },
        // ações presas à direita: a tabela é larga e rola na horizontal
        actionsHead: {
            position: 'sticky',
            right: 0,
            zIndex: 3,
            boxShadow: `-1px 0 0 ${t.line}`
        },
        actionsCell: {
            position: 'sticky',
            right: 0,
            zIndex: 1,
            width: '1%',
            backgroundColor: t.paper,
            boxShadow: `-1px 0 0 ${t.line}`
        }
    };
});

// Recursos liga/desliga do plano (mesmos campos booleanos de antes)
const PLAN_FLAGS = [
    { name: 'useWhatsapp', label: () => 'Whatsapp' },
    { name: 'useWhatsappOfficial', label: () => 'Whatsapp Oficial' },
    { name: 'useFacebook', label: () => 'Facebook' },
    { name: 'useInstagram', label: () => 'Instagram' },
    { name: 'useCampaigns', label: () => i18n.t("plans.form.campaigns") },
    { name: 'useSchedules', label: () => i18n.t("plans.form.schedules") },
    { name: 'useInternalChat', label: () => 'Chat Interno' },
    { name: 'useExternalApi', label: () => 'API Externa' },
    { name: 'useKanban', label: () => 'Kanban' },
    { name: 'useOpenAi', label: () => 'Talk.Ai' },
    { name: 'useIntegrations', label: () => 'Integrações' },
    { name: 'wavoip', label: () => 'Wavoip' }
];

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()
    const panel = usePanelStyles()
    const { user } = useContext(AuthContext)
    // Revenda: preço por empresa ativa (planos de revenda) — só o super edita
    const showPerActiveCompany = isSuperUser(user)

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        perActiveCompanyAmount: '',
        aiMonthlyCredit: 0,
        aiOverageMultiplier: 2,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false
    });

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(formik) => {
                // chave liga/desliga: grava booleano, como as opções Sim/Não de antes
                const renderSwitch = (name, label, checked) => (
                    <SwitchRow key={name} label={label}>
                        <Switch
                            size="small"
                            color="primary"
                            name={name}
                            checked={checked}
                            onChange={(e) => formik.setFieldValue(name, e.target.checked)}
                            inputProps={{ 'aria-label': label }}
                        />
                    </SwitchRow>
                )
                return (
                <Form className={classes.fullWidth}>
                    <Section title={record.id !== undefined ? 'Editar plano' : 'Novo plano'}>
                        <FormSection title="Identificação">
                            <div className={classes.formGrid}>
                                {/* NOME */}
                                <FormField label={i18n.t("plans.form.name")} htmlFor="plan-name" className={classes.nameField}>
                                    <Field
                                        as={TextField}
                                        id="plan-name"
                                        name="name"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                </FormField>

                                {/* PUBLICO */}
                                <FormField label={i18n.t("plans.form.public")}>
                                    {renderSwitch('isPublic', formik.values.isPublic ? 'Sim' : 'Não', Boolean(formik.values.isPublic))}
                                </FormField>

                                {/* TRIAL */}
                                <FormField label="Trial">
                                    {renderSwitch('trial', formik.values.trial ? 'Sim' : 'Não', Boolean(formik.values.trial))}
                                </FormField>

                                {/* TRIAL DAYS */}
                                <FormField label={i18n.t("plans.form.trialDays")} htmlFor="plan-trialDays">
                                    <Field
                                        as={TextField}
                                        id="plan-trialDays"
                                        name="trialDays"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        disabled={!formik.values.trial}
                                    />
                                </FormField>
                            </div>
                        </FormSection>

                        <FormSection title="Limites e preço">
                            <div className={classes.formGrid}>
                                {/* USUARIOS */}
                                <FormField label={i18n.t("plans.form.users")} htmlFor="plan-users">
                                    <Field
                                        as={TextField}
                                        id="plan-users"
                                        name="users"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                    />
                                </FormField>

                                {/* CONEXOES */}
                                <FormField label={i18n.t("plans.form.connections")} htmlFor="plan-connections">
                                    <Field
                                        as={TextField}
                                        id="plan-connections"
                                        name="connections"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                    />
                                </FormField>

                                {/* FILAS */}
                                <FormField label="Filas" htmlFor="plan-queues">
                                    <Field
                                        as={TextField}
                                        id="plan-queues"
                                        name="queues"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                    />
                                </FormField>

                                {/* VALOR */}
                                <FormField label="Valor" htmlFor="plan-amount">
                                    <Field
                                        as={TextField}
                                        id="plan-amount"
                                        name="amount"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="text"
                                    />
                                </FormField>

                                {/* VALOR POR EMPRESA ATIVA (revenda) — só super */}
                                {showPerActiveCompany && (
                                    <FormField
                                        label={i18n.t("plans.form.perActiveCompanyAmount")}
                                        htmlFor="plan-perActiveCompanyAmount"
                                        help={i18n.t("plans.form.perActiveCompanyAmountHelper")}
                                        className={classes.nameField}
                                    >
                                        <Field
                                            as={TextField}
                                            id="plan-perActiveCompanyAmount"
                                            name="perActiveCompanyAmount"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            type="text"
                                            inputProps={{ inputMode: "decimal" }}
                                        />
                                    </FormField>
                                )}
                            </div>
                        </FormSection>

                        {/* IA: crédito mensal e teto (medição por empresa) */}
                        <FormSection title="Inteligência artificial">
                            <div className={classes.formGrid}>
                                <FormField label={i18n.t("aiUsage.plan.credit")} htmlFor="plan-aiMonthlyCredit">
                                    <Field
                                        as={TextField}
                                        id="plan-aiMonthlyCredit"
                                        name="aiMonthlyCredit"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: "0.01" }}
                                    />
                                </FormField>
                                <FormField label={i18n.t("aiUsage.plan.multiplier")} htmlFor="plan-aiOverageMultiplier">
                                    <Field
                                        as={TextField}
                                        id="plan-aiOverageMultiplier"
                                        name="aiOverageMultiplier"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 1, step: "0.1" }}
                                    />
                                </FormField>
                            </div>
                        </FormSection>

                        <FormSection title="Recursos" description="Ligue o que o plano libera para a empresa.">
                            <div className={classes.flagsGrid}>
                                {PLAN_FLAGS.map((flag) =>
                                    renderSwitch(flag.name, flag.label(), formik.values[flag.name] === true)
                                )}
                            </div>
                        </FormSection>

                        <div className={classes.formActions}>
                            {record.id !== undefined ? (
                                <ButtonWithSpinner
                                    loading={loading}
                                    onClick={() => onDelete(record)}
                                    className={`${panel.busyWrap} ${panel.danger}`}
                                >
                                    {i18n.t("plans.form.delete")}
                                </ButtonWithSpinner>
                            ) : null}
                            <div className={classes.formActionsRight}>
                                <ButtonWithSpinner loading={loading} onClick={() => onCancel()}>
                                    {i18n.t("plans.form.clear")}
                                </ButtonWithSpinner>
                                <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                                    {i18n.t("plans.form.save")}
                                </ButtonWithSpinner>
                            </div>
                        </div>
                    </Section>
                </Form>
                )
            }}
        </Formik>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect, canEditPlan = () => true } = props
    const classes = useStyles()
    const panel = usePanelStyles()

    const renderName = (row) => (
        <span className={classes.nameCell}>
            <span className={panel.cellStrong}>{row.name || '-'}</span>
            {isPlatformPlan(row) ? (
                <StatusBadge color="brand" size="sm" label={i18n.t("plans.form.platformPlan")} />
            ) : null}
        </span>
    )

    // recurso do plano: só `false` desliga (igual ao Sim/Não de antes)
    const renderFlag = (value) => {
        const on = value !== false
        const text = on ? `${i18n.t("plans.form.yes")}` : `${i18n.t("plans.form.no")}`
        return on
            ? <CheckIcon className={classes.flagOn} titleAccess={text} />
            : <RemoveIcon className={classes.flagOff} titleAccess={text} />
    }

    return (
        <Paper className={`${panel.tableCard} ${panel.tableCardAuto}`} elevation={0}>
            <Table className={classes.table} size="small" aria-label="Planos">
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.colName}>{i18n.t("plans.form.name")}</TableCell>
                        <TableCell>{i18n.t("plans.form.public")}</TableCell>
                        <TableCell align="right">{i18n.t("plans.form.trial")}</TableCell>
                        <TableCell align="right">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell align="right">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell align="right">Filas</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        {PLAN_FLAGS.map((flag) => (
                            <TableCell key={flag.name} align="center">{flag.label()}</TableCell>
                        ))}
                        <TableCell align="right" className={classes.actionsHead}>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>{renderName(row)}</TableCell>
                            <TableCell>
                                <StatusBadge
                                    size="sm"
                                    dot={false}
                                    color={row.isPublic ? 'success' : 'neutral'}
                                    label={row.isPublic ? "Sim" : "Não"}
                                />
                            </TableCell>
                            <TableCell align="right">{row.trialDays || '-'}</TableCell>
                            <TableCell align="right">{row.users || '-'}</TableCell>
                            <TableCell align="right">{row.connections || '-'}</TableCell>
                            <TableCell align="right">{row.queues || '-'}</TableCell>
                            <TableCell align="right">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}</TableCell>
                            {PLAN_FLAGS.map((flag) => (
                                <TableCell key={flag.name} align="center">{renderFlag(row[flag.name])}</TableCell>
                            ))}
                            <TableCell className={classes.actionsCell}>
                                <span className={panel.rowActions}>
                                    {canEditPlan(row) ? (
                                        <Tooltip title="Editar">
                                            <IconButton
                                                size="small"
                                                onClick={() => onSelect(row)}
                                                aria-label="Editar plano"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        // Plano da plataforma (somente-leitura para revendedor):
                                        // botão desabilitado não dispara eventos, então o Tooltip
                                        // vai num <span> envolvente.
                                        <Tooltip title={i18n.t("plans.form.readOnly")} arrow>
                                            <span>
                                                <IconButton size="small" aria-label="Editar plano" disabled>
                                                    <EditIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                    {records.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8 + PLAN_FLAGS.length} className={panel.empty}>
                                Nenhum plano cadastrado ainda.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()
    const { user } = useContext(AuthContext)
    const isSuper = isSuperUser(user)
    // Revenda: revendedor só edita/apaga os próprios planos; os da plataforma são somente-leitura
    const canEditPlan = (plan) => isSuper || !isPlatformPlan(plan)

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        perActiveCompanyAmount: '',
        aiMonthlyCredit: 0,
        aiOverageMultiplier: 2,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegrations: true,
        isPublic: true,
        useWhatsappOfficial: false,
        trial: false,
        trialDays: 3,
        recurrence: 'monthly',
        wavoip: false
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    // "" / null → null; "15,00" → 15 (número). Revendedor não vê o campo: não envia a chave.
    const normalizePerActive = (raw) => {
        if (raw === undefined || raw === null || raw === '') return null
        const n = Number(String(raw).replace(/\./g, '').replace(',', '.'))
        return Number.isFinite(n) && n >= 0 ? n : null
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        const payload = { ...data }
        payload.aiMonthlyCredit = Math.max(0, Number(String(data.aiMonthlyCredit ?? 0).replace(',', '.')) || 0)
        payload.aiOverageMultiplier = Number(String(data.aiOverageMultiplier ?? 2).replace(',', '.')) || 2
        if (isSuper) {
            payload.perActiveCompanyAmount = normalizePerActive(data.perActiveCompanyAmount)
        } else {
            delete payload.perActiveCompanyAmount
        }
        try {
            if (payload.id !== undefined) {
                await update(payload)
            } else {
                await save(payload)
            }
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            id: undefined,
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            perActiveCompanyAmount: '',
            aiMonthlyCredit: 0,
            aiOverageMultiplier: 2,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            useKanban: true,
            useOpenAi: true,
            useIntegrations: true,
            isPublic: true,
            useWhatsappOfficial: false,
            trial: false,
            trialDays: 3,
            recurrence: 'monthly',
            wavoip: false
        })
    }

    const handleSelect = (data) => {
        // Plano da plataforma: o botão de editar já vem desabilitado (Tooltip na grid)
        if (!canEditPlan(data)) return

        let useWhatsapp = data.useWhatsapp === false ? false : true
        let useWhatsappOfficial = data.useWhatsappOfficial === false ? false : true
        let useFacebook = data.useFacebook === false ? false : true
        let useInstagram = data.useInstagram === false ? false : true
        let useCampaigns = data.useCampaigns === false ? false : true
        let useSchedules = data.useSchedules === false ? false : true
        let useInternalChat = data.useInternalChat === false ? false : true
        let useExternalApi = data.useExternalApi === false ? false : true
        let useKanban = data.useKanban === false ? false : true
        let useOpenAi = data.useOpenAi === false ? false : true
        let useIntegrations = data.useIntegrations === false ? false : true
        let wavoip = data.wavoip === false ? false : true

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            perActiveCompanyAmount:
                data.perActiveCompanyAmount === null || data.perActiveCompanyAmount === undefined
                    ? ''
                    : Number(data.perActiveCompanyAmount).toLocaleString('pt-br', { minimumFractionDigits: 2 }),
            aiMonthlyCredit: Number(data.aiMonthlyCredit) || 0,
            aiOverageMultiplier: Number(data.aiOverageMultiplier) || 2,
            useWhatsapp,
            useWhatsappOfficial,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useOpenAi,
            useIntegrations,
            isPublic: data.isPublic,
            trial: data.trial,
            trialDays: data.trialDays,
            recurrence: data.recurrence,
            wavoip
        })
    }

    return (
        <div className={classes.root}>
            <PlanManagerForm
                initialValue={record}
                onDelete={handleOpenDeleteDialog}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                loading={loading}
            />
            <PlansManagerGrid
                records={records}
                onSelect={handleSelect}
                canEditPlan={canEditPlan}
            />
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </div>
    )
}
